import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FileText, MessageSquareX, CalendarClock, PackageX, UserX, PhoneCall, ArrowRight, Check, Circle, Play } from 'lucide-react';

const Icons = { FileText, MessageSquareX, CalendarClock, PackageX, UserX, PhoneCall, ArrowRight, Check, Circle };
import { API } from '../../../lib/api';
import { Reveal, Counter, Laptop, Phone, DemoButton, Eyebrow, H2 } from './ui';
import { FitDiagram, FlowDiagram } from './Diagrams';
import { TourDialog, TOUR_POSTER } from './TourDialog';
import { SCREENS, SEGMENTS, PAINS, SHOWCASES, GST_FLOW } from './content';

export const useProof = () => {
  const [proof, setProof] = useState(null);
  useEffect(() => { axios.get(`${API}/public/driveline/proof`).then((r) => setProof(r.data)).catch(() => {}); }, []);
  return proof;
};

const TourButton = ({ onOpen }) => (
  <button type="button" onClick={onOpen} className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-[#cbd5e1] py-2 pl-2 pr-6 text-[15px] font-semibold text-[#0f172a] transition-colors hover:border-[#0f172a]" data-testid="hero-watch-tour">
    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0f766e] text-white transition-transform group-hover:scale-110"><Play size={15} fill="currentColor" /></span>
    Watch 90-sec tour
  </button>
);

const TourThumb = ({ onOpen }) => (
  <button type="button" onClick={onOpen} className="group absolute bottom-0 right-0 hidden w-[38%] overflow-hidden rounded-xl border-4 border-white shadow-2xl sm:block" aria-label="Play the product tour" data-testid="hero-tour-thumb">
    <img src={TOUR_POSTER} alt="Driveline product tour" className="block aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105" />
    <span className="absolute inset-0 flex items-center justify-center bg-[rgba(15,23,42,.35)]">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#0f766e] shadow-lg transition-transform group-hover:scale-110"><Play size={20} fill="currentColor" /></span>
    </span>
  </button>
);

export const Hero = () => {
  const [tour, setTour] = useState(false);
  const open = () => setTour(true);
  return (
  <section className="relative overflow-hidden pb-16 pt-28 sm:pt-32" data-testid="home-hero">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_1.15fr]">
      <Reveal>
        <Eyebrow>For detailing, body shop and service workshops</Eyebrow>
        <h1 className="dl-display mt-4 text-4xl font-semibold leading-[1.02] tracking-tight text-[#0f172a] sm:text-5xl lg:text-6xl">
          Run your whole workshop from one screen.
          <span className="block text-[#0f766e]">Built inside a working workshop.</span>
        </h1>
        <p className="mt-6 max-w-xl text-base text-[#475569] md:text-lg">Job cards, GST invoices, inspections, parts, warranty and payroll in one place. Made on our own floor in Bangalore, now running workshops across India.</p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <DemoButton testid="hero-book-demo" />
          <TourButton onOpen={open} />
        </div>
      </Reveal>
      <Reveal delay={150} className="relative pb-10 lg:pl-6">
        <Laptop src={SCREENS.jobcard} alt="Driveline job card on a laptop" className="dl-tilt" />
        <Phone src={SCREENS.jobcardPhone} alt="Driveline job card on a phone" className="absolute -bottom-2 left-0 w-[26%] min-w-[110px] sm:-left-4" />
        <TourThumb onOpen={open} />
      </Reveal>
    </div>
    {tour && <TourDialog onClose={() => setTour(false)} />}
  </section>
  );
};

const PROOF_ITEMS = [['job_cards', 'job cards opened'], ['invoices', 'invoices raised'], ['inspections', 'inspections done'], ['warranties', 'warranty certificates']];

export const ProofStrip = ({ proof }) => (
  <section className="border-y border-[#e7e5df] bg-[#ffffff]" data-testid="proof-strip">
    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-5 py-10 sm:px-8 lg:grid-cols-5">
      {PROOF_ITEMS.filter(([k]) => !proof || proof.counts?.[k] > 0).map(([k, label]) => (
        <div key={k}>
          <p className="dl-display text-3xl font-semibold text-[#0f172a] sm:text-4xl"><Counter value={proof?.counts?.[k] || 0} testid={`proof-${k}`} />+</p>
          <p className="mt-1 text-sm text-[#475569]">{label}</p>
        </div>
      ))}
      <p className="col-span-2 self-center text-sm font-semibold text-[#0f766e] lg:col-span-1">On our own floor since {proof?.since || '2024'}. Real numbers, updated daily.</p>
    </div>
  </section>
);

export const SegmentTiles = () => (
  <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-testid="segments">
    <Eyebrow>Who it’s for</Eyebrow>
    <H2 className="max-w-2xl">Made for the way your kind of workshop runs.</H2>
    <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {SEGMENTS.map((s, i) => (
        <Reveal key={s.slug} delay={i * 90}>
          <Link to={`/driveline/solutions/${s.slug}`} className="group relative block aspect-[4/5] overflow-hidden rounded-3xl" data-testid={`segment-tile-${s.slug}`}>
            <img src={s.image} alt={s.tile} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/90 via-[#0f172a]/20 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5">
              <h3 className="dl-display text-xl font-semibold text-[#f8fafc]">{s.tile}</h3>
              <p className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-[#5eead4]">See how <Icons.ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></p>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

export const Pains = () => (
  <section className="bg-[#efece4] py-20 sm:py-28" data-testid="pains">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Eyebrow>Sound familiar?</Eyebrow>
      <H2 className="max-w-2xl">Six things every workshop owner knows too well.</H2>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PAINS.map((p, i) => {
          const Icon = Icons[p.icon] || Icons.Circle;
          return (
            <Reveal key={p.pain} delay={i * 70} className="rounded-3xl bg-[#ffffff] p-6" data-testid={`pain-${i}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#fee2e2] text-[#b91c1c]"><Icon size={20} /></span>
              <h3 className="dl-display mt-5 text-xl font-semibold text-[#0f172a]">{p.pain}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#475569]"><b className="text-[#0f766e]">Driveline:</b> {p.fix}</p>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const HowItFits = () => (
  <section id="how-it-works" className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-testid="how-it-fits">
    <Eyebrow>How it fits together</Eyebrow>
    <H2 className="max-w-2xl">Four parts of the business, one record of every car.</H2>
    <div className="mt-14"><FitDiagram /></div>
  </section>
);

const Showcase = ({ s, i }) => (
  <div className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 ? 'lg:[&>*:first-child]:order-2' : ''}`} data-testid={`showcase-${i}`}>
    <Reveal><Laptop src={s.screen} alt={s.title} /></Reveal>
    <Reveal delay={120}>
      <h3 className="dl-display text-3xl font-semibold leading-tight text-[#0f172a] sm:text-4xl">{s.title}</h3>
      <ul className="mt-6 space-y-3">
        {s.points.map((pt) => <li key={pt} className="flex gap-3 text-[15px] text-[#334155]"><Icons.Check size={18} className="mt-0.5 shrink-0 text-[#0f766e]" />{pt}</li>)}
      </ul>
      {s.diagram === 'gst' && <div className="mt-8 rounded-2xl bg-[#ffffff] p-5"><FlowDiagram steps={GST_FLOW} testid="gst-flow" /></div>}
      <Link to={`/driveline/solutions/${s.more}`} className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0f766e] hover:text-[#0f172a]" data-testid={`showcase-more-${i}`}>Read more <Icons.ArrowRight size={14} /></Link>
    </Reveal>
  </div>
);

export const Showcases = () => (
  <section className="bg-[#efece4] py-20 sm:py-28" data-testid="showcases">
    <div className="mx-auto max-w-7xl space-y-24 px-5 sm:px-8">
      {SHOWCASES.map((s, i) => <Showcase key={s.title} s={s} i={i} />)}
    </div>
  </section>
);
